import { GridColumn } from '@progress/kendo-react-grid'
import { useSetAtom } from 'jotai'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { createBreadcrumbWithI18n } from '~/apps/applicationAdmin/routes/paths'
import {
  PageContainer,
  type PageContainerProps,
} from '~/components/elements/PageContainer'
import {
  StyledButton,
  type StyledButtonProps,
} from '~/components/elements/StyledButton'
import { StyledGrid } from '~/components/elements/StyledGrid'
import { VStack } from '~/components/elements/VStack'
import { useLanguageTextWidth } from '~/hooks/useLanguageTextWidth'

import { dialogInfoAtom } from '../atoms'
import { useMainQueryCoreMembers } from '../hooks/useMainQuery'
import { pathKey } from '../utils'

import { CommandsCell } from './CommandsCell'
import { Dialogs } from './Dialogs'

export const CoreMembersList = () => {
  const coreMembers = useMainQueryCoreMembers()
  const { t } = useTranslation()

  const createBreadcrumb = React.useMemo(() => createBreadcrumbWithI18n(t), [t])
  const breadcrumbs: PageContainerProps['breadcrumbs'] = React.useMemo(
    () => [createBreadcrumb('top', {}), createBreadcrumb(pathKey, {})],
    [createBreadcrumb],
  )

  const setDialogInfo = useSetAtom(dialogInfoAtom)
  const handleAddButtonClick = React.useCallback<
    EH<StyledButtonProps, 'onClick'>
  >(() => {
    setDialogInfo({ dialogType: 'add' })
  }, [setDialogInfo])

  const commandButtonWidth = useLanguageTextWidth({ 'ja-JP': 82, 'en-US': 96 })
  // padding + button + padding
  const commandsCellWidth = 12 + commandButtonWidth + 12

  return (
    <PageContainer breadcrumbs={breadcrumbs}>
      <VStack align="end" spacing={2} verticalAppearance="stretch">
        <StyledButton
          buttonStyle="success"
          fillMode="outline"
          icon="add"
          onClick={handleAddButtonClick}
        >
          {t('add')}
        </StyledButton>

        <StyledGrid data={coreMembers}>
          <GridColumn field="email" title={t('email_address')} />

          <GridColumn
            field="lastLoginAt"
            format="{0:yyyy/MM/dd HH:mm:ss}"
            title={t('last_login_date')}
          />

          <GridColumn cell={CommandsCell} width={commandsCellWidth} />
        </StyledGrid>

        <Dialogs />
      </VStack>
    </PageContainer>
  )
}
